import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent } from '@theme/dialog';

const WRAP_PROP = '_gift_wrapping';
const GIFT_PROP = '_gift_packaging';
const GIFT_QTY_PROP = '_gift_packaging_qty';
const SOURCE = 'gift-wrapping';

let busy = false;
let pendingEnsure = false;
/** @type {Map<string, number>} */
const pendingUpdates = new Map();
let retryTimer;

function getTheme() {
  return window.Theme || {};
}

function getConfig() {
  const giftWrapping = getTheme().giftWrapping || {};
  const fromDom = document.querySelector('[data-variant-id].gift-wrapping, gift-wrapping-checkbox[data-variant-id]');
  const variantId = Number(giftWrapping.variantId || fromDom?.getAttribute('data-variant-id') || 0) || null;
  const enabled = Boolean(variantId && giftWrapping.enabled !== false);
  return {
    enabled,
    variantId,
    productId: giftWrapping.productId ? Number(giftWrapping.productId) : null,
    handle: giftWrapping.handle || null,
  };
}

function formatMoney(cents) {
  const giftWrapping = getTheme().giftWrapping || {};
  const format = giftWrapping.moneyFormat || '{{amount}}';
  const hideTrailingZeros = Boolean(giftWrapping.hideTrailingZeros);
  const value = Math.max(0, Number(cents) || 0);
  const amount = (value / 100).toFixed(2);
  const amountNoDecimals = String(Math.round(value / 100));
  const displayAmount = hideTrailingZeros && value % 100 === 0 ? amountNoDecimals : amount;

  return format
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimals)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
    .replace(/\{\{\s*amount\s*\}\}/g, displayAmount);
}

function cartAddUrl() {
  return getTheme().routes?.cart_add_url || '/cart/add.js';
}

function cartChangeUrl() {
  return getTheme().routes?.cart_change_url || '/cart/change.js';
}

function getSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
}

function giftPropertyValue(item) {
  const properties = item?.properties;
  if (!properties) return '';
  return properties[GIFT_PROP] || '';
}

function isWrapItem(item, config) {
  if (!item) return false;
  if (config.variantId && Number(item.variant_id) === Number(config.variantId)) return true;
  if (config.productId && Number(item.product_id) === Number(config.productId)) return true;
  if (config.handle && item.handle === config.handle) return true;
  return false;
}

function isPhysicalItem(item, config) {
  if (isWrapItem(item, config)) return false;
  if (item.gift_card) return false;
  return true;
}

function lineGiftQuantity(item, config) {
  if (!isPhysicalItem(item, config)) return 0;

  const max = Number(item.quantity) || 0;
  const qtyProp = item?.properties?.[GIFT_QTY_PROP];
  const parsed = Number.parseInt(String(qtyProp || ''), 10);
  if (Number.isFinite(parsed) && parsed > 0) {
    return Math.min(parsed, max);
  }
  if (giftPropertyValue(item) === 'Yes') {
    return max;
  }
  return 0;
}

function isGiftedItem(item, config) {
  return lineGiftQuantity(item, config) > 0;
}

function findWraps(cart, config) {
  return (cart.items || []).filter((item) => isWrapItem(item, config));
}

function findWrap(cart, config) {
  return findWraps(cart, config)[0] || null;
}

function giftedQuantity(cart, config) {
  return (cart.items || []).reduce((sum, item) => sum + lineGiftQuantity(item, config), 0);
}

function isFullCart(cart) {
  return Boolean(cart && Array.isArray(cart.items) && cart.items.every((item) => typeof item?.key === 'string'));
}

async function fetchCart() {
  const response = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
  return response.json();
}

function dispatchCartEvent(data, isAdd) {
  const detail = {
    source: SOURCE,
    sections: data.sections,
    itemCount: data.item_count,
  };
  const event = isAdd
    ? new CartAddEvent(data, SOURCE, detail)
    : new CartUpdateEvent(data, SOURCE, detail);
  document.dispatchEvent(event);
}

function cloneProperties(item) {
  const properties = item?.properties && typeof item.properties === 'object' ? item.properties : {};
  /** @type {Record<string, string>} */
  const next = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value == null || value === '') continue;
    next[key] = String(value);
  }
  return next;
}

async function addWrap(variantId, quantity) {
  const body = JSON.stringify({
    items: [
      {
        id: variantId,
        quantity,
        properties: { [WRAP_PROP]: 'Yes' },
      },
    ],
    sections: getSectionIds().join(','),
    sections_url: window.location.pathname,
  });

  const response = await fetch(cartAddUrl(), fetchConfig('json', { body }));
  const data = await response.json();
  if (data.status) {
    console.error(data);
    return null;
  }
  dispatchCartEvent(data, true);
  return data;
}

async function changeWrap(key, quantity) {
  const body = JSON.stringify({
    id: key,
    quantity,
    sections: getSectionIds().join(','),
    sections_url: window.location.pathname,
  });

  const response = await fetch(cartChangeUrl(), fetchConfig('json', { body }));
  const data = await response.json();
  if (data.errors) {
    console.error(data);
    return null;
  }
  dispatchCartEvent(data, false);
  return data;
}

async function changeLineGift(item, giftQty) {
  const max = Number(item.quantity) || 0;
  const nextQty = Math.max(0, Math.min(Number(giftQty) || 0, max));
  const properties = cloneProperties(item);

  if (nextQty > 0) {
    properties[GIFT_PROP] = 'Yes';
    properties[GIFT_QTY_PROP] = String(nextQty);
  } else {
    properties[GIFT_PROP] = '';
    properties[GIFT_QTY_PROP] = '';
  }

  const body = JSON.stringify({
    id: item.key,
    quantity: item.quantity,
    properties,
    sections: getSectionIds().join(','),
    sections_url: window.location.pathname,
  });

  const response = await fetch(cartChangeUrl(), fetchConfig('json', { body }));
  const data = await response.json();
  if (data.errors) {
    console.error(data);
    return null;
  }
  dispatchCartEvent(data, false);
  return data;
}

async function syncWrapQuantity(cart, config) {
  const wrap = findWrap(cart, config);
  const giftedQty = giftedQuantity(cart, config);

  if (giftedQty <= 0) {
    if (wrap) return changeWrap(wrap.key, 0);
    return cart;
  }
  if (!wrap) return addWrap(config.variantId, giftedQty);
  if (wrap.quantity !== giftedQty) return changeWrap(wrap.key, giftedQty);
  return cart;
}

/**
 * Keep wrap quantity equal to gifted physical units; remove wrap when none are gifted.
 * @param {{ ensure?: boolean, cart?: object }} [options]
 */
export async function reconcileGiftWrapping(options = {}) {
  const config = getConfig();
  if (!config.enabled) return;
  if (options.ensure) pendingEnsure = true;
  if (busy) return;

  busy = true;
  setControlsDisabled(true);

  try {
    while (true) {
      pendingEnsure = false;

      const cart = isFullCart(options.cart) ? options.cart : await fetchCart();
      options.cart = null;

      const wraps = findWraps(cart, config);
      const wrap = wraps[0] || null;

      if (wraps.length > 1) {
        await changeWrap(wraps[1].key, 0);
        continue;
      }

      const overCap = (cart.items || []).find((item) => {
        const stored = Number.parseInt(String(item?.properties?.[GIFT_QTY_PROP] || ''), 10);
        return Number.isFinite(stored) && stored > (Number(item.quantity) || 0);
      });
      if (overCap) {
        await changeLineGift(overCap, overCap.quantity);
        continue;
      }

      const giftedQty = giftedQuantity(cart, config);

      if (giftedQty <= 0) {
        if (wrap) {
          await changeWrap(wrap.key, 0);
          continue;
        }
        break;
      }

      if (!wrap) {
        await addWrap(config.variantId, giftedQty);
        continue;
      }

      if (wrap.quantity !== giftedQty) {
        await changeWrap(wrap.key, giftedQty);
        continue;
      }

      break;
    }
  } catch (error) {
    console.error(error);
  } finally {
    busy = false;
    setControlsDisabled(false);
    if (pendingUpdates.size) {
      flushPendingUpdates();
    } else if (pendingEnsure) {
      reconcileGiftWrapping({ ensure: true });
    }
  }
}

function setControlsDisabled(disabled) {
  document.querySelectorAll('gift-wrapping-checkbox').forEach((el) => {
    el.classList.toggle('is-busy', disabled);
    const checkbox = el.querySelector('.gift-wrapping__input');
    if (checkbox instanceof HTMLInputElement) checkbox.disabled = disabled;
  });
}

class GiftWrappingCheckbox extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('.gift-wrapping__input');
    this.qtyWrap = this.querySelector('.gift-wrapping__qty');
    this.qtyInput = this.querySelector('[data-gift-qty-input]');
    this.minusButton = this.querySelector('[data-gift-qty-minus]');
    this.plusButton = this.querySelector('[data-gift-qty-plus]');
    this.linePrice = this.querySelector('[data-gift-line-price]');

    if (!(this.checkbox instanceof HTMLInputElement)) return;

    this.onCheckboxChange = () => {
      const context = this.dataset.context;
      if (context === 'product') return;

      if (context === 'line') {
        const maxQty = this.#maxQty();
        const nextQty = this.checkbox.checked ? maxQty : 0;
        this.#syncQtyUi(nextQty);
        this.#queueLineUpdate(nextQty);
        return;
      }

      if (this.checkbox.checked) {
        reconcileGiftWrapping({ ensure: true });
      } else {
        pendingEnsure = false;
        removeWrapFromCart();
      }
    };

    this.onMinus = () => this.#adjustQty(-1);
    this.onPlus = () => this.#adjustQty(1);
    this.onQtyInput = () => this.#commitQtyInput();
    this.onQtyKeydown = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.#commitQtyInput();
      }
    };

    this.checkbox.addEventListener('change', this.onCheckboxChange);
    this.minusButton?.addEventListener('click', this.onMinus);
    this.plusButton?.addEventListener('click', this.onPlus);
    this.qtyInput?.addEventListener('change', this.onQtyInput);
    this.qtyInput?.addEventListener('blur', this.onQtyInput);
    this.qtyInput?.addEventListener('keydown', this.onQtyKeydown);
    this.#syncQtyUi(this.#currentQty());
  }

  disconnectedCallback() {
    this.checkbox?.removeEventListener('change', this.onCheckboxChange);
    this.minusButton?.removeEventListener('click', this.onMinus);
    this.plusButton?.removeEventListener('click', this.onPlus);
    this.qtyInput?.removeEventListener('change', this.onQtyInput);
    this.qtyInput?.removeEventListener('blur', this.onQtyInput);
    this.qtyInput?.removeEventListener('keydown', this.onQtyKeydown);
  }

  #maxQty() {
    return Math.max(1, Number(this.dataset.maxQty) || 1);
  }

  #currentQty() {
    if (!this.checkbox?.checked) return 0;
    return Number(this.qtyInput?.value) || this.#maxQty();
  }

  #adjustQty(delta) {
    const maxQty = this.#maxQty();
    const nextQty = Math.min(maxQty, Math.max(1, this.#currentQty() + delta));
    this.checkbox.checked = true;
    this.#syncQtyUi(nextQty);
    this.#queueLineUpdate(nextQty);
  }

  #commitQtyInput() {
    if (!(this.qtyInput instanceof HTMLInputElement)) return;
    const maxQty = this.#maxQty();
    const nextQty = Math.min(maxQty, Math.max(1, Number.parseInt(this.qtyInput.value, 10) || 1));
    this.checkbox.checked = true;
    this.#syncQtyUi(nextQty);
    this.#queueLineUpdate(nextQty);
  }

  #syncQtyUi(qty) {
    const maxQty = this.#maxQty();
    const enabled = qty > 0;
    if (this.qtyWrap instanceof HTMLElement) this.qtyWrap.hidden = !enabled;
    if (this.qtyInput instanceof HTMLInputElement) {
      this.qtyInput.value = String(enabled ? qty : 1);
      this.qtyInput.max = String(maxQty);
    }
    if (this.minusButton instanceof HTMLButtonElement) this.minusButton.disabled = !enabled || qty <= 1;
    if (this.plusButton instanceof HTMLButtonElement) this.plusButton.disabled = !enabled || qty >= maxQty;
    if (this.linePrice instanceof HTMLElement) {
      this.linePrice.hidden = !enabled;
      if (enabled) {
        const unitCents = Number(this.dataset.unitCents) || 0;
        this.linePrice.textContent = formatMoney(unitCents * qty);
      }
    }
  }

  #queueLineUpdate(qty) {
    const lineKey = this.dataset.lineKey;
    if (!lineKey) return;
    pendingUpdates.set(lineKey, qty);
    flushPendingUpdates();
  }
}

async function flushPendingUpdates() {
  const config = getConfig();
  if (!config.enabled || busy || pendingUpdates.size === 0) return;

  busy = true;
  setControlsDisabled(true);

  try {
    while (pendingUpdates.size) {
      const lineKey = pendingUpdates.keys().next().value;
      const requestedQty = pendingUpdates.get(lineKey);
      pendingUpdates.delete(lineKey);

      const cart = await fetchCart();
      const item = (cart.items || []).find((line) => line.key === lineKey);
      if (!item || isWrapItem(item, config) || item.gift_card) continue;

      const nextQty = Math.max(0, Math.min(Number(requestedQty) || 0, Number(item.quantity) || 0));
      if (lineGiftQuantity(item, config) === nextQty) continue;

      const updated = await changeLineGift(item, nextQty);
      if (updated && isFullCart(updated)) {
        await syncWrapQuantity(updated, config);
      }
    }
  } catch (error) {
    console.error(error);
  } finally {
    busy = false;
    setControlsDisabled(false);
    if (pendingUpdates.size) {
      flushPendingUpdates();
    } else if (pendingEnsure) {
      reconcileGiftWrapping({ ensure: true });
    }
  }
}

async function removeWrapFromCart() {
  const config = getConfig();
  if (!config.enabled || busy) return;

  busy = true;
  setControlsDisabled(true);
  try {
    const cart = await fetchCart();
    const wrap = findWrap(cart, config);
    if (wrap) await changeWrap(wrap.key, 0);
  } catch (error) {
    console.error(error);
  } finally {
    busy = false;
    setControlsDisabled(false);
  }
}

function scheduleReconcile(event) {
  if (event?.detail?.data?.source === SOURCE) return;
  if (event?.detail?.source === SOURCE) return;

  const cart = event?.detail?.resource;
  reconcileGiftWrapping({ cart: isFullCart(cart) ? cart : null });

  window.clearTimeout(retryTimer);
  retryTimer = window.setTimeout(() => {
    reconcileGiftWrapping();
  }, 400);
}

document.addEventListener(ThemeEvents.cartUpdate, scheduleReconcile);
document.addEventListener('gift-wrapping:ensure', () => reconcileGiftWrapping({ ensure: true }));

if (!customElements.get('gift-wrapping-checkbox')) {
  customElements.define('gift-wrapping-checkbox', GiftWrappingCheckbox);
}

class GiftWrappingInfoDialog extends DialogComponent {
  #nested = false;
  #lockedBody = null;
  #patched = false;

  connectedCallback() {
    super.connectedCallback();
    if (this.#patched) return;
    this.#patched = true;

    const originalShow = this.showDialog.bind(this);
    const originalClose = this.closeDialog;

    this.showDialog = () => {
      const dialog = this.refs?.dialog;
      this.#nested = [...document.querySelectorAll('dialog[open]')].some((openDialog) => openDialog !== dialog);
      this.#lockedBody = this.#nested
        ? {
            width: document.body.style.width,
            position: document.body.style.position,
            top: document.body.style.top,
          }
        : null;

      originalShow();
      this.#syncTriggers(true);

      if (this.#nested) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.#restoreBody());
        });
      }
    };

    this.closeDialog = async () => {
      await originalClose();
      this.#restoreBody();
      this.#syncTriggers(false);
      this.#nested = false;
      this.#lockedBody = null;
    };
  }

  #restoreBody() {
    if (!this.#lockedBody) return;
    document.body.style.width = this.#lockedBody.width;
    document.body.style.position = this.#lockedBody.position;
    document.body.style.top = this.#lockedBody.top;
  }

  #syncTriggers(open) {
    document.querySelectorAll('[data-gift-wrapping-info-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }
}

if (!customElements.get('gift-wrapping-info-dialog')) {
  customElements.define('gift-wrapping-info-dialog', GiftWrappingInfoDialog);
}

reconcileGiftWrapping();
